import { SpendLevel } from '../../../../../../../../frontend/libs/shared/src/lib/types/types';
import * as buyers from './buyers.service';
import * as categories from './categories.service';

export interface ContractChoice {
  name: string;
  value: string | number;
  supports: boolean;
}

export interface ContractOnRfp {
  buyer: {
    id: number;
    hasSigned: boolean;
    signedBy: {
      id: number;
      name: string;
    };
    name: string;
    daysToSign: number;
    contractSignedOn: string;
    purchasePlan?: any;
    masterPlannedMarketShare?: any;
    plannedSpendLevel?: any;
    masterPlannedSpendLevel?: any;
  };
  dualSource: boolean;
  duration: number;
  effectiveDate: string;
  id: number;
  marketShare: number;
  numberOfRebates: number;
  possibleEffectiveDates: string[];
  pricingType: string;
  seller: {
    hasValidCpaTemplate: boolean;
    id: number;
    leadTimeDays: number;
    hasSigned: boolean;
    signedBy: {
      id: number;
      name: string;
    };
    name: string;
    daysToSign: number;
    contractSignedOn: string;
    offerId: number;
  };
  status: string;
  supportedOfferTerm: {
    dualSourceCharacteristicId: number;
    durations: Array<ContractChoice>;
    pricingTypes: Array<ContractChoice>;
  };
  targetSpendLevel?: any;
  commitmentType: string;
}

// This is slightly different from Contract
// Contract gets returned by GET api/contracts/{contractId}
// ContractsOnRfp gets returned by GET api/contracts/
export interface ContractsOnRfp {
  category: categories.Category;
  buyer: {
    id: number;
    aggreementStatus: string; // typo in backend service
    categorySpend: buyers.CategorySpend;
    isPartner: boolean;
    memberId: number;
    name: string;
  };
  isPricingExpired: boolean;
  rfpStatus: string;
  contracts: ContractOnRfp[];
  rfpId: number;
}

export interface ContractPeriod {
  start: {
    month: number;
    year: number;
  };
  end: {
    month: number;
    year: number;
  };
}

export interface ContractLastMeasuredCompliancePeriod {
  complianceLevel: string;
  marketShare: number;
  marketShareAdjustment: {
    adjustmentPeriodDueDate: number;
    canRequestAdjustment: boolean;
    noticePeriodDueDate: number;
    requestedByOrganization: string;
    requestedBy: {
      id: number;
      name: string;
    };
  };
  period: ContractPeriod;
}

export interface ContractBuyer {
  id: number;
  hasSigned: boolean;
  signedBy: {
    id: number;
    name: string;
  };
  name: string;
  daysToSign: number;
  contractSignedOn: string;
}

export interface ContractSeller {
  hasValidCpaTemplate: boolean;
  id: number;
  leadTimeDays: number;
  hasSigned: boolean;
  signedBy: {
    id: number;
    name: string;
  };
  name: string;
  daysToSign: number;
  contractSignedOn: string;
  offerId: number;
}

export interface ContractMeasurement {
  performance: number;
  entity: {
    id: number;
    name: string;
  };
  marketShare: number;
  spend: {
    totalSpend: number;
    spendWithSeller: number;
  };
  belowTargetMarketShare: boolean;
  unitBelowTargetMarketShare: boolean;
}

export interface ContractMeasurements {
  rampUpMeasurement: {
    participantsMeasurements: ContractMeasurement[];
    targetMarketshare: number;
    period: ContractPeriod;
    unspscsMeasurements: ContractMeasurement[];
  };
  periodMeasurements: [
    {
      participantsMeasurements: ContractMeasurement[];
      targetMarketshare: number;
      period: ContractPeriod;
      unspscsMeasurements: ContractMeasurement[];
    },
  ];
  yearMeasurements: [
    {
      measurements: [
        {
          participantsMeasurements: ContractMeasurement[];
          yearMonth: ContractYearMonth;
          buyerMeasurement: {
            yearMonth: ContractYearMonth;
            measurement: {
              isRampUp: boolean;
              isPreliminary: boolean;
              marketShare: {
                cummulative: number;
                currentMonth: number;
              };
              spend: {
                cummulative: {
                  totalSpend: number;
                  spendWithSeller: number;
                };
                currentMonth: {
                  totalSpend: number;
                  spendWithSeller: number;
                };
              };
            };
          };
          unspscsMeasurements: ContractMeasurement[];
        },
      ];
      monthEnd: number;
      year: number;
      monthStart: number;
    },
  ];
}

export interface Document {
  fileName: string;
  fileType: string;
  link: string;
  effectiveDate: number;
}

export interface IMonthYear {
  month: number;
  year: number;
}

interface IMonthYearRange {
  start: IMonthYear;
  end: IMonthYear;
}

export interface IPeriodRange {
  period: IMonthYearRange;
  rampUpPeriod: boolean;
  isPreContract: boolean;
  targetMarketShare: number;
}

export interface IPeriodMonthYear {
  buyerMeasurement: {
    measurement: {
      isPreliminary: boolean;
      isRampUp: boolean;
      isPreContract: boolean;
      complianceLevel?: string; // Pre-SDT
    };
  };
  yearMonth: IMonthYear;
}

export type IPeriod = IPeriodRange | IPeriodMonthYear;

// Type Guards
export function isPeriodRange(period): period is IPeriodRange {
  return period && period.period !== undefined;
}

export function isPeriodMonthYear(period): period is IPeriodMonthYear {
  return period && period.yearMonth !== undefined;
}

interface Spend_Result {
  belowTargetParticipantCount: number;
  complianceLevel: string;
  marketShare: number;
  spendWithSeller: number;
  totalSpend: number;
}

// There are other properties available, but we should treat them as deprecated and only use these
interface SDT_Measurement {
  dollarResult: Spend_Result;
  unitResult: Spend_Result;
  participantCount: number;
  period: ContractPeriod;
  targetSpendLevel: SpendLevel;
  incrementalTargetSpendLevel?: SpendLevel;
}

export interface Contract {
  agreementType: string;
  buyer: ContractBuyer;
  category: categories.Category; // is this right?
  characteristics: any;
  contractAvailableForDownload: boolean;
  duration: number;
  effectiveDate: string;
  enhancedAnalyticsOverview?: EnhancedAnalyticsOverview;
  expirationDate: string;
  hasEnhancedAnalytics: boolean;
  id: number;
  isGroupRfp: boolean;
  isIndividualCommitment: boolean;
  isMultiSource: boolean; // Isn't this also a characteristic? Do we need this?
  members: {
    id: number;
    name: string;
  }[];
  numberOfRebates: number;
  participantCount: number;
  possibleEffectiveDates: string[];
  pricingType: string;
  publishedId: string; // It's a number returned as a string
  seller: ContractSeller;
  spend: any;
  status: string;
  varianceReports: {
    id: number;
    period: ContractPeriod;
  }[];
  // This is the last completed compliance period (if any)
  complianceMeasurement: SDT_Measurement;
  // This is the current compliance period
  cumulativeMeasurement: SDT_Measurement;
  initialTargetSpendLevel: {
    absoluteDollars: number | null;
    absoluteUnits: number | null;
    dollarMarketShare: number | null;
    type: any;
    unitMarketShare: number | null;
  };
  spendLevelAdjustment: {
    requestedByBuyer: boolean;
    canRequestTargetSpendLevelAdjustment: boolean;
    targetSpendLevelAdjustmentNotificationDue: number;
    requestTargetSpendLevelAdjustmentDue: number;
  };
  measurements: ContractMeasurements; // Does this look different for SDT?
  // Only populated for partners
  partner?: {
    measurements: ContractMeasurements;
    cumulativeMeasurement?: SDT_Measurement;
    complianceMeasurement?: SDT_Measurement;
    spendLevelType: any;
    targetSpendLevel: any;
  };
  spendLevelType: {
    name: string;
    displayName: string;
    fieldNames: string[];
    operator?: string; // not using this yet
  };
  targetSpendLevel: SpendLevel;
}

/**
 * Is it an Individual Commit contract?
 */
export function isIndividualCommit(contract: Contract): boolean {
  return contract && contract.isGroupRfp && contract.isIndividualCommitment;
}

export interface ContractYearMonth {
  month: number;
  year: number;
}

export interface PurchaseAnalysis {
  averageMarketShare: number;
  historicalMarketShare: number;
  forecastMarketShare: number;
}

export interface EnhancedAnalyticsOverview {
  spendDollarAmount: PurchaseAnalysis;
  unitCountPurchased: PurchaseAnalysis;
  contractMeasurableDateRange: any;
  lastTwelveMonthsTimeframe: any;
  forecastTimeframe: {
    end: any;
  };
}

/**
 * This service may change drastically for phase2 of Save Plan
 */
export default class ContractsService {
  static $inject = ['$http'];

  baseUrl = '/aptitude/api/contracts';

  constructor(private $http) {
    'ngInject';
  }

  getContracts(rfpId: number): ng.IPromise<ContractsOnRfp> {
    return this.$http.get(this.baseUrl, {
      params: {
        rfpId,
      },
    });
  }

  requestMarketShareAdjustment(contractId: number) {
    return this.$http.post(`${this.baseUrl}/${contractId}/changerequests`, {
      attribute: 'MARKETSHARE',
    });
  }

  getDocuments(contractId: number) {
    return this.$http.get(`${this.baseUrl}/${contractId}/documents`);
  }

  getPartnerContract(
    contractId: number,
    memberId: number,
    config?: any,
  ): ng.IPromise<Contract> {
    const shouldCache = config && 'cache' in config ? config.cache : true; // default to true if not set

    return this.$http.get(
      `${this.baseUrl}/${contractId}/partners/${memberId}`,
      {
        cache: shouldCache,
      },
    );
  }

  getAnalytics(contractId: number, config?: any): ng.IPromise<Contract> {
    const shouldCache = config && 'cache' in config ? config.cache : true; // default to true if not set

    return this.$http.get(`${this.baseUrl}/${contractId}/analytics`, {
      cache: shouldCache,
    });
  }

  /**
   * Mem
   * @param  {number}           contractId [description]
   * @param  {number}           memberId   Either the sellerId or systemId
   * @param  {number}           facilityId The facilityId (optional if systemId is provided)
   * @return {ng.IPromise<any>}            [description]
   */
  getProducts(
    contractId: number,
    memberId?: number,
    facilityId?: number,
    aptDex = false,
  ): ng.IPromise<any> {
    // /contracts/contractId/products/systemId/facilityId
    let url = `${this.baseUrl}/${contractId}/products`;

    if (memberId != null) {
      url += `/${memberId}`;

      if (facilityId != null) {
        url += `/${facilityId}`;
      }
    }

    if (aptDex) {
      url += '?fields=aptDex';
    }

    return this.$http.get(url);
  }

  getAptDexProducts(
    contractId: number,
    memberId?: number,
    facilityId?: number,
  ) {
    return this.getProducts(contractId, memberId, facilityId, true);
  }

  /**
   * Get the list of competitors
   * @param {number} contractId
   */
  getCompetitors(contractId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${contractId}/products/competitors`);
  }

  getPartners(contractId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${contractId}/partners`);
  }

  getPeriods(contractId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${contractId}/periods`);
  }

  getUnspscs(contractId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${contractId}/unspscs`);
  }

  getSystemsFacilitiesMap(contractId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${contractId}/products/facilities`);
  }

  deleteCharacteristic(contractId: number, characteristicId: number) {
    return this.$http.delete(
      `${this.baseUrl}/${contractId}/characteristics/${characteristicId}`,
    );
  }

  updateCharacteristic(contractId: number, key: string, value: string) {
    const characteristic = {
      characteristics: [
        {
          key,
          value,
        },
      ],
    };

    return this.$http.post(
      `${this.baseUrl}/${contractId}/characteristics`,
      characteristic,
    );
  }
}
